/**
 * The cut/fill colour convention, in ONE place (T4, closing F21).
 *
 * The owner's ruling: **cut is red, fill is blue, everywhere** — the section
 * chart's bands and hatches, the 3D corridor bands, the volumes chart's bars,
 * the mass-haul emphasis, the deviation colorbar, the deviation cloud's ramp,
 * the surface-difference GLB and GeoTIFF, the per-station render, the PDF
 * section drawing and the deviation PDF histogram. F21 asked whether the
 * section drawing and the render on one PDF page could colour the same band
 * oppositely; they could, and this module is the answer.
 *
 * What actually made that possible is that "cut" is not a sign — it is a sign
 * PER ANALYSIS, and the two analyses disagree:
 *
 *  - `deviation`: the point cloud minus the design model, so a POSITIVE value
 *    is material sitting above the design — cut.
 *  - `surfaceDifference`: `dz = top − bottom`
 *    (`scripts/surface_difference.py`: "dz > 0 → fill body, dz < 0 → cut
 *    body"), so a NEGATIVE value is cut.
 *
 * Every colour surface derives its polarity from `signIsCut` / `cutEnd` and
 * its default ramp from `defaultRampName` / `defaultGradientName` rather than
 * hard-coding a ramp name, because a hard-coded name is exactly how the two
 * halves of a station page drifted apart in the first place.
 *
 * The report worker carries a twin of this file
 * (`server/background/src/functions/processReport/cutFillConvention.ts`) — it
 * cannot import from the app. Keep the two in step; the twin holds one
 * boolean and one helper and no arithmetic of its own.
 */

/** The two signed quantities the product colours. */
export type SignedSurfaceKind = 'deviation' | 'surfaceDifference';

/**
 * The ruling itself, as a value, so a reader of any consuming module can find
 * the decision rather than infer it from a hex code. Nothing flips on it
 * being `false` — it is a statement, not a switch; a future reversal would
 * change the ramps and the copy below, and this constant is what a reader
 * greps for to find them all.
 */
export const CUT_IS_RED = true;

/** The two sentences of the copy rule, and the short forms for tight rows. */
export const CUT_LEGEND_KEY = 'stationVolumes.legend.cut';
export const FILL_LEGEND_KEY = 'stationVolumes.legend.fill';
export const CUT_LEGEND_SHORT_KEY = 'stationVolumes.legend.cutShort';
export const FILL_LEGEND_SHORT_KEY = 'stationVolumes.legend.fillShort';

/**
 * Is `value` on the cut side of zero for this kind of run?
 *
 * Exactly zero is neither: a cell where the two surfaces meet is not material
 * to move, and painting it as cut would put a hue on the one value the
 * diverging ramps deliberately leave neutral. `-0` answers the same as `0`
 * (`-0 < 0` is already false, and `-0 > 0` likewise) — the Python writers
 * normalise `-0.0` away for the same reason.
 */
export function signIsCut(kind: SignedSurfaceKind, value: number): boolean {
  return kind === 'deviation' ? value > 0 : value < 0;
}

/**
 * Which end of a `[min, max]` colour bar is the cut end — the end that must
 * be red, and the end the "Cut · above design" words belong beside.
 *
 * Derived from `signIsCut` rather than restated, so the two can never
 * disagree.
 */
export function cutEnd(kind: SignedSurfaceKind): 'min' | 'max' {
  return signIsCut(kind, 1) ? 'max' : 'min';
}

/**
 * The default matplotlib ramp for a signed quantity: one diverging ramp,
 * oriented so that the cut end is red.
 *
 * `Spectral_r` runs blue → red, so its red end is the HIGH end — right for
 * deviation. `Spectral` runs red → blue, so its red end is the LOW end —
 * right for surface difference. They are the same ramp; only the orientation
 * differs, which is the whole point: a reader comparing a deviation page with
 * a surface-difference page sees one palette meaning one thing.
 *
 * This replaces the `jet` fallbacks that used to litter the run dialogs and
 * the report input. `jet` is a rainbow: it invents an ordering the data does
 * not have and puts its most salient band (yellow) at an arbitrary value —
 * the same objection `bandGeometry.ts` records against `RAINBOW`.
 */
export function defaultRampName(kind: SignedSurfaceKind): string {
  return cutEnd(kind) === 'max' ? 'Spectral_r' : 'Spectral';
}

/**
 * The default expressed as a VIEWER PALETTE name (`@/lib/colormaps`'s `STOPS`
 * keys). One name for both kinds, and deliberately so.
 *
 * `pointCloudMaterialColor` and `surfaceDifferenceColormap` are persisted as
 * `PointColorType` (`graphql/layer.gql`), which enumerates PALETTES —
 * `SPECTRAL`, `VIRIDIS`, … — and nothing else. A reversed member would not be
 * in that enum: picking it fails validation and discards the whole settings
 * save. So a stored palette carries no orientation, and `rampPosition` below
 * supplies it per kind instead.
 */
export function defaultGradientName(_kind: SignedSurfaceKind): string {
  return 'SPECTRAL';
}

/**
 * The value re-expressed so that CUT is always the larger number.
 *
 * The whole orientation rule in one line: **the palette's high end is cut**. A
 * deviation is already that way round (positive = cloud above design = cut); a
 * surface difference is `dz = top − bottom`, so it is negated.
 */
export function orientForRamp(kind: SignedSurfaceKind, value: number): number {
  return signIsCut(kind, 1) ? value : -value;
}

/**
 * Where `value` sits on a palette drawn over the display range `[min, max]`,
 * as `t ∈ [0, 1]`, with the CUT end at `t = 1`.
 *
 * Every ramped surface asks this rather than dividing by the range itself.
 * That is what lets one stored palette — diverging OR single-hue, the
 * viewer's default or one the user picked — put cut at its strong end for both
 * kinds of run, without inventing a palette name the enum does not have.
 *
 * Clamps rather than wraps: values beyond the display range saturate at the
 * ramp's ends, as the workers' own ramps do, and wrapping would paint the
 * deepest cut in the fill hue. A zero-width range yields the cut end rather
 * than a NaN.
 */
export function rampPosition(kind: SignedSurfaceKind, value: number, min: number, max: number): number {
  const lo = orientForRamp(kind, min);
  const hi = orientForRamp(kind, max);
  // `orientForRamp` may have swapped the ends, so the span is taken from the
  // oriented pair, not from `max - min`.
  const span = Math.max(lo, hi) - Math.min(lo, hi);
  if (!(span > 0)) return 1;
  return Math.min(1, Math.max(0, (orientForRamp(kind, value) - Math.min(lo, hi)) / span));
}
